<script>
import { mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default{
  name: "EditChunkModal",
  props:{
    chunk: {
      type: Object
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    }
  },
  data(){
    return{
      isLoading: false,
      isExpanded: false,
      content: "",
      keywords: [],
      isAdding: false,
      newKeyword: '',
    }
  },
  methods:{
    ...mapActions('dify', ['updateChunk', 'createChunk']),

    handleRemoveKeyword(keyword) {
      this.keywords = this.keywords.filter(k => k !== keyword);
    },

    handleAddKeyword(keyword) {
      if (this.keywords.includes(keyword)) {
        useAlert('Keyword already existed');
        return;
      }
      this.keywords.push(keyword);
    },

    addKeyword() {
      const keyword = this.newKeyword.trim();
      if (keyword) {
        this.handleAddKeyword(keyword);
      }
      this.resetInput();
    },
    
    cancelAdd() {
      this.resetInput();
    },

    resetInput() {
      this.newKeyword = '';
      this.isAdding = false;
    },

    async handleUpdate(){
      this.isLoading = true;
      try{
        const payload = {
          content: this.content,
          keywords: this.keywords,
        };
        await this.updateChunk({
          datasetId: this.$route.params.id,
          documentId: this.$route.params.documentId,
          segmentId: this.chunk.id,
          payload: payload,
        });
        await new Promise(resolve => { setTimeout(resolve, 1500); });
        useAlert("Chunk updated");
        this.$emit('close');
        this.$emit('refresh');
      }catch(err){
        console.error(err);
        useAlert(err.message || 'Failed to update chunk');
      }finally{
        this.isLoading = false;
      }
    },

    async handleCreate(){
      try{
        this.isLoading = true;
        const payload = {
          content: this.content,
          keywords: this.keywords,
        }
        await this.createChunk({
          datasetId: this.$route.params.id,
          documentId: this.$route.params.documentId,
          payload: payload,
        });
        await new Promise(resolve => { setTimeout(resolve, 1500); });
        useAlert("Chunk created");
        this.$emit('close');
        this.$emit('refresh');
      }catch(err){
        console.error(err);
        useAlert(err.message || 'Failed to create chunk');
      }finally{
        this.isLoading = false;
      }
    }
  },
  mounted(){
    if(!this.isCreating){
      this.content = this.chunk.content;
      this.keywords = this.chunk.keywords;
    }
  }
}
</script>

<template>
  <div class="fixed inset-0 z-40 flex items-center justify-center p-4">
    <!-- Backdrop -->
    <div
      v-if="!isExpanded"
      class="fixed inset-0 z-40 bg-slate-800/20 dark:bg-slate-800/50"
    ></div>

    <!-- Modal -->
    <div
      class="bg-white dark:bg-slate-700 dark:text-slate-100 p-4 rounded-md fixed z-50 transition-all duration-300 ease-in-out flex flex-col"
      :class="[
        isExpanded
          ? 'w-screen h-screen top-0 left-0'
          : 'w-[560px] h-[500px] top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2'
      ]"
      data-testid="edit_chunk_modal"
      :data-read-only="readOnly"
      @click.stop
    >
    <div v-if="!isExpanded" class="flex gap-4 absolute top-2 right-4">
        <woot-button
          variant="clear"
          color-scheme="secondary"
          :icon="isExpanded ? 'arrow-minimize' : 'arrow-maximize'"
          size="small"
          data-testid="edit_chunk_expand_button"
          @click="isExpanded = !isExpanded"
          :title="isExpanded ? $t('KNOWLEDGE.VIEW_CHUNKS.COLLAPSE_CHUNKS') : $t('KNOWLEDGE.VIEW_CHUNKS.EXPAND_CHUNKS')"
        />
        <woot-button
          variant="clear"
          color-scheme="secondary"
          icon="dismiss"
          size="small"
          data-testid="edit_chunk_close_button"
          @click="$emit('close')"
          :title="$t('KNOWLEDGE.EDITCHUNKMODAL.CANCEL_BUTTON')"
        />
      </div>

      <div class="w-full flex justify-between">
        <header class="mb-4">
          <h4 class="text-lg font-semibold" data-testid="edit_chunk_title">{{ readOnly ? $t('KNOWLEDGE.EDITCHUNKMODAL.VIEW_TITLE') : ( isCreating ? $t('KNOWLEDGE.EDITCHUNKMODAL.CREATE_TITLE') : $t('KNOWLEDGE.EDITCHUNKMODAL.EDIT_TITLE') ) }} {{ $t('KNOWLEDGE.EDITCHUNKMODAL.CHUNK_TITLE') }}</h4>
          <p v-if="isCreating" class="text-sm text-gray-500 dark:text-gray-300">{{ $t('KNOWLEDGE.EDITCHUNKMODAL.NEW_CHUNK') }} · {{ content.length }} {{ $t('KNOWLEDGE.EDITCHUNKMODAL.CHARACTERS_COUNT') }}</p>
          <p v-else-if="readOnly" class="text-sm text-gray-500 dark:text-gray-300">
            {{
              chunk.position < 10
                ? $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_LIGHTRAG_PADDED', { position: chunk.position.toString(), word_count: chunk.true_word_count })
                : $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_LIGHTRAG', { position: chunk.position.toString(), word_count: chunk.true_word_count })
            }}
          </p>
          <p v-else class="text-sm text-gray-500 dark:text-gray-300">
            {{
              chunk.position < 10
                ? $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO_PADDED', { position: chunk.position.toString(), word_count: chunk.true_word_count, hit_count: chunk.hit_count })
                : $t('KNOWLEDGE.CHUNK_LIST_ITEM.CHUNK_INFO', { position: chunk.position.toString(), word_count: chunk.true_word_count, hit_count: chunk.hit_count })
            }}
          </p>
        </header>

        <div v-if="isExpanded" class="flex gap-4 items-center">
          <template v-if="!readOnly">
            <woot-button variant="clear" color-scheme="secondary" data-testid="edit_chunk_cancel_button" @click="$emit('close')">{{ $t('KNOWLEDGE.EDITCHUNKMODAL.CANCEL_BUTTON') }}</woot-button>
            <woot-button :disabled="isLoading" variant="clear" color-scheme="primary" data-testid="edit_chunk_save_button" @click="isCreating ? handleCreate() : handleUpdate()">
              {{ isLoading ? $t('KNOWLEDGE.EDITCHUNKMODAL.LOADING_STATE') : ( isCreating ? $t('KNOWLEDGE.EDITCHUNKMODAL.CREATE_BUTTON') : $t('KNOWLEDGE.EDITCHUNKMODAL.SAVE_BUTTON') ) }}
            </woot-button>
          </template>
          <woot-button
            variant="clear"
            color-scheme="secondary"
            :icon="isExpanded ? 'arrow-minimize' : 'arrow-maximize'"
            size="small"
            data-testid="edit_chunk_expand_button"
            @click="isExpanded = !isExpanded"
          />
          <woot-button
            variant="clear"
            color-scheme="secondary"
            icon="dismiss"
            size="small"
            data-testid="edit_chunk_close_button"
            @click="$emit('close')"
          />
        </div>
      </div>
      <textarea
        class="flex-grow bg-transparent h-full w-full overflow-y-auto resize-none outline-none text-text-secondary dark:text-slate-100 placeholder:text-text-secondary dark:placeholder:text-slate-400 caret-[#295EFF] pb-6 tracking-tight"
        :class="{ 'cursor-default': readOnly }"
        :placeholder="$t('KNOWLEDGE.EDITCHUNKMODAL.ADD_CONTENT_PLACEHOLDER')"
        :readonly="readOnly"
        data-testid="edit_chunk_textarea"
        v-model="content"
      />

      <div v-if="!readOnly || keywords.length > 0" class="flex flex-wrap gap-2">
        <div
          v-for="keyword in keywords"
          :key="keyword"
          class="flex gap-2 pl-2 rounded-full items-center border-2 border-slate-500 dark:border-slate-300"
          data-testid="edit_chunk_keyword_chip"
        >
          <p class="text-xs mb-0">{{ keyword }}</p>
          <woot-button v-if="!readOnly" size="tiny" color-scheme="secondary" variant="clear" icon="dismiss" data-testid="edit_chunk_keyword_remove_button" @click="handleRemoveKeyword(keyword)" class="rounded-full"></woot-button>
        </div>
        <div v-if="!readOnly" class="flex gap-2 pl-2 rounded-full items-center border-2 border-slate-500 dark:border-slate-300">
          <template v-if="isAdding">
            <input
              v-model="newKeyword"
              @keydown.enter="addKeyword"
              @blur="cancelAdd"
              class="bg-transparent text-xs dark:text-slate-100 focus:outline-none w-24"
              autofocus
              data-testid="edit_chunk_add_keyword_input"
            />
          </template>
          <template v-else>
            <p class="text-xs mb-0">{{ $t('KNOWLEDGE.EDITCHUNKMODAL.ADD_KEYWORD_LABEL') }}</p>
            <woot-button
              size="tiny"
              color-scheme="secondary"
              variant="clear"
              icon="plus-sign"
              data-testid="edit_chunk_add_keyword_button"
              @click="isAdding = true"
              class="rounded-full"
            />
          </template>
        </div>
      </div>
      <div v-if="!isExpanded" class="flex justify-end gap-4 mt-4">
        <woot-button variant="clear" color-scheme="secondary" data-testid="edit_chunk_cancel_button" @click="$emit('close')">{{ readOnly ? $t('KNOWLEDGE.EDITCHUNKMODAL.CLOSE_BUTTON') : $t('KNOWLEDGE.EDITCHUNKMODAL.CANCEL_BUTTON') }}</woot-button>
        <woot-button
          v-if="!readOnly"
          variant="clear"
          color-scheme="primary"
          data-testid="edit_chunk_save_button"
          @click="isCreating ? handleCreate() : handleUpdate()"
          :disabled="isLoading"
        >{{ isLoading ? $t('KNOWLEDGE.EDITCHUNKMODAL.LOADING_STATE') : ( isCreating ? $t('KNOWLEDGE.EDITCHUNKMODAL.CREATE_BUTTON') : $t('KNOWLEDGE.EDITCHUNKMODAL.SAVE_BUTTON') ) }}</woot-button>
      </div>
    </div>
  </div>
</template>
